import { Component, inject } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbarModule } from '@angular/material/toolbar';
import { DocumentoService } from '../../../services/documento.service';
import { AuthService } from '../../../services/auth.service';
import { HttpErrorResponse } from '@angular/common/http';
import { DlgAlertComponent } from '../../dlg-alert/dlg-alert.component';
import { AutofocusDirective } from '../../../helpers/autofocus.directive';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Firma } from '../../../models/Firma';
import { TokenData } from '../../../models/User';
import { DlgSignatureComponent } from '../dlg-signature.component';

interface FirmaData{
  x:number
  y:number
  adj:number
  pagina:number
}

@Component({
    selector: 'app-btn-sign',
    imports: [
        MatDialogModule,
        MatRadioModule,
        MatButtonModule,
        MatDialogActions,
        MatDialogClose,
        MatDialogContent,
        MatIconModule,
        MatToolbarModule,
        MatFormFieldModule,
        MatSelectModule,
        MatInputModule,
        FormsModule,
        AutofocusDirective,
        ReactiveFormsModule,
        MatProgressSpinnerModule
    ],
    templateUrl: './btn-sign.component.html',
    styleUrl: './btn-sign.component.scss'
})
export class BtnSignComponent {
  hide = true;

  txtClave = new FormControl('', [Validators.required]);

  firmaDefault?: Firma;
  firmas: Firma[] = [];
  
  authServ = inject(AuthService);
  tokenData: TokenData = this.authServ.getTokenData()!;

  readonly firmaData = inject<FirmaData>(MAT_DIALOG_DATA);

  fetchingSignDocument = false

  constructor(
    private documentoServ: DocumentoService,
    public dlg: MatDialog,
    private dialogRef: MatDialogRef<DlgSignatureComponent>
  ) {
    documentoServ.getFirmasList().subscribe({
      next: (res: Firma[]) => {
        this.firmas = res;
        this.firmaDefault = this.firmas.find((firma) => firma.ciruc === this.tokenData.sub)
        
        if (!this.firmaDefault && this.firmas.length > 0) {
          this.firmaDefault = this.firmas.at(0)
        }
      },
    });
  }

  onAceptar() {
    this.fetchingSignDocument = true
    if (this.txtClave.value?.trim().length === 0) {
      this.txtClave.setValue('');
      this.fetchingSignDocument = false
      return;
    }

    this.firmaDefault!.contrasena = this.txtClave.value!;
    this.firmaDefault!.x = this.firmaData.x!;
    this.firmaDefault!.y = this.firmaData.y!;
    this.firmaDefault!.pagina = this.firmaData.pagina!;

    this.documentoServ
      .signDocumento(this.firmaData.adj, this.firmaDefault).subscribe({
        next: (_) => {
          this.fetchingSignDocument = false
          this.dialogRef.close(true);
        },
        error: (err: HttpErrorResponse) => {
          this.fetchingSignDocument = false
          this.dlg.open(DlgAlertComponent, {
            data: {
              title: 'Error',
              message: this.getSignErrorMessage(err)
            }
          });
        }
      });
  }

  private getSignErrorMessage(err: HttpErrorResponse): string {
    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;

    if (err.status === 401 || err.status === 403) {
      return 'La clave de firma electronica es incorrecta. Verifique e intente nuevamente.';
    }

    if (err.status === 0) {
      if (isOffline) {
        return 'No fue posible conectar con el servicio de firma. Verifique su conexion e intente nuevamente.';
      }
      return 'La clave de firma electronica es incorrecta. Verifique e intente nuevamente.';
    }

    if (err.status >= 500) {
      return 'La clave de firma electronica es incorrecta. Verifique e intente nuevamente.';
    }

    return 'No fue posible firmar el documento. Intente nuevamente.';
  }

  private stripHtml(value: string): string {
    if (!value) {
      return '';
    }

    return value
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  clickEvent(event: MouseEvent) {
    this.hide = !this.hide;
    event.stopPropagation();
  }
}
